import type { EmbedTreeResponse } from "../types/tree";

export interface FetchTreeOptions {
  /** Base URL of the deployment (e.g. "https://dk3.3dconfig.dk"). Empty = same origin. */
  baseUrl?: string;
  /** Tree GUID. */
  treeId: string;
  /** Optional Unity target — when set, the response includes a viewer build URL. */
  target?: string;
  /** Unity deployment stage. */
  stage?: "Development" | "Staging" | "Production";
  /**
   * Author-only flag. When true, asks the server for the unpruned tree (drafts +
   * dead branches included). Server only honors it for dashboard-authenticated
   * requests (same-origin cookie); anonymous embeds always get the published view.
   */
  includeUnpublished?: boolean;
  signal?: AbortSignal;
}

/**
 * Fetches the full tree + all referenced products in one call. After this, the React
 * tool walks the tree locally — no further API calls per click.
 *
 * Auth: published trees are anonymous, drafts require a same-origin dashboard cookie.
 * Sent with credentials:"include" so the cookie attaches automatically when present.
 */
export async function fetchTree(opts: FetchTreeOptions): Promise<EmbedTreeResponse> {
  const params = new URLSearchParams();
  if (opts.target) params.set("target", opts.target);
  if (opts.stage) params.set("stage", opts.stage);
  if (opts.includeUnpublished) params.set("includeUnpublished", "true");

  const base = (opts.baseUrl ?? "").replace(/\/+$/, "");
  const qs = params.toString();
  const url = `${base}/api/embed/tree/${encodeURIComponent(opts.treeId)}${qs ? "?" + qs : ""}`;

  const res = await fetch(url, {
    headers: { Accept: "application/json" },
    credentials: "include",
    signal: opts.signal,
  });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(`fetchTree failed: ${res.status} ${res.statusText} ${text}`);
  }
  return (await res.json()) as EmbedTreeResponse;
}
